// A LIVE BRANCH IS SKINNED BY THE BODY RIG, not carried by one bone.
//
// A live branch owns its vertices — they are its deform's output — so the body
// used to reach it as ONE rigid delta on its anchor (`partBodyPose.js`). That is
// right for a head and wrong for what a MetaHuman `head_lod0` carries below it:
// the neck, and a cap of the shoulders, which belong to the SPINE, so the head's
// full rotation hinges them out of the character's clothes at the collar. No
// rigid transform can be right for both — measured against the rig's own skinned
// answer for the same vertices, the collar is out by a median worst 25 mm on a
// head turn and by ~250 mm on the walk clips, which is the head dropping into
// the torso the moment she walks.
//
// It does not have to be: such a branch also has a part in `rig.glb`, so the
// deform gives the EXPRESSION and the skinning the CARRY, the way a face rig and
// a body rig always compose.
//
// WHY IT LIVES HERE, beside `partBodyPose.js` and `headRigidDrive.js` rather
// than in a render path's component. It was written inside `OGSModel.jsx`, so
// the OTHER path never installed it and its live branches silently took the
// rigid-anchor fallback — a detached head on every walk, found by a person
// looking at the screen. Installed from `buildPartDrive`, which both paths
// already call, a live branch is skinned BY CONSTRUCTION and a future path
// inherits it without knowing it exists. `renderContract.js` reports the day it
// does not.

import { createGpuPartSkinner, MAX_SKIN_DISAGREEMENT_METRES } from '../inference/GpuPartSkinner'
import { ogsGpuFlags } from '../inference/ogsGpuFlags'

/**
 * Give every live branch a `bodySkin()` that skins it with the body rig.
 *
 * A branch that cannot take it keeps the rigid anchor and SAYS WHICH REASON —
 * "no part in rig.glb", "this lift has no GPU vertex buffer", or a failed
 * identity check. Degrading stays available; degrading in silence does not.
 *
 * @param {object} o
 * @param {object} o.driver    the part driver (`driver.verts.skinPlan(name)`)
 * @param {Array}  o.branches  the bundle's branches
 * @param {(b: import('../graph/nodes/OGSBranch.ts').OGSBranch) => boolean} o.isLive
 * @param {GPUDevice|null} o.device
 * @param {object} [o.log]
 * @returns {Promise<Map<string, object>>} the skinners, for disposal by the caller
 */
export async function installLiveBodySkin({ driver, branches, isLive, device, log = console }) {
  const done = new Map()
  // ?meshview draws `branch.verts`, and a GPU-skinned branch never writes them —
  // the posed vertices go straight to the lifter's buffer and that array keeps
  // its neutral pose. Left on, a mesh view would draw a NEUTRAL body beside
  // walking splats and look like proof of a bug that is really the overlay
  // reading the wrong array.
  if (!device || ogsGpuFlags().meshView !== 'off') return done
  for (const br of branches) {
    if (!isLive(br)) continue
    const plan = driver.verts.skinPlan?.(br.name)
    const lifter = br.lifter
    if (!plan || !lifter?.vertsBuffer || typeof lifter.setVertsGpu !== 'function') {
      log.info?.(`[ogs] '${br.name}': the body cannot skin this branch `
        + `(${!plan ? 'no part in rig.glb' : 'this lift has no GPU vertex buffer'}) — anchored instead`)
      continue
    }
    let skinner = null
    try {
      skinner = createGpuPartSkinner(device, plan)
      if (!skinner) throw new Error('the plan carries no vertices')
      skinner.setSourceVerts(lifter.vertsBuffer)
      // Parity, at the one pose where the answer is known: the rig is at rest here and
      // the lifter holds the branch's neutral, so skinning it must give the neutral
      // back — linear blend skinning is the identity at the bind pose, which is the
      // same statement as the part fit's own "neutral mean=0.0mm". A transposed or
      // mis-scaled inverse in the unplace pass fails here rather than as a subtly
      // wrong head nobody can see is wrong.
      const delta = await skinner.verifyIdentity()
      if (!(delta <= MAX_SKIN_DISAGREEMENT_METRES)) {
        throw new Error('skinning at the rest pose moves the branch '
          + `${delta == null ? 'an unknown amount' : `${(delta * 1000).toFixed(2)} mm`}`)
      }
      br.bodySkin = () => { const out = skinner.skin(); if (out) lifter.setVertsGpu(out) }
      // setVertsGpu runs the jacobians on the SKINNED verts, so the pose's own pass
      // would only compute them for vertices nothing ever lifts.
      lifter.deferJacobians = true
      done.set(br.name, skinner)
    } catch (e) {
      skinner?.dispose()
      br.bodySkin = null
      log.error?.(`[ogs] '${br.name}': body skinning refused (${e?.message ?? e}) — anchored instead`)
    }
  }
  if (done.size) log.info?.(`[ogs] body-skinned live branches: ${[...done.keys()].join(', ')}`)
  return done
}
